/**
 * 左上の「← ○○」を「来た画面へ戻る」にするフック群（PRODUCT_DEV-1043）。記録の本体は `lib/nav/backTracker`。
 *
 * 使い分け:
 * - 新しい画面は SubHeader に任せる（自動でこれを使う）
 * - 画面の中の「保存」「キャンセル」で来た画面へ戻すときは `useBackNav().back()` を呼ぶ
 * - 来た画面へ戻さない例外: 管理画面（/admin 以下）・商談詳細の編集中（`fixedBack`）・顧客レポートの ✕（`backIcon`）・
 *   録音前のカウントダウン・削除後の遷移（消した記録へ戻らないよう一覧へ）
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useRouteLoaderData } from "react-router";
import { useOptionalHomeNav } from "@/components/shared/chrome/ChromeContext";
import { ROUTE_MEMO, ROUTE_TOP } from "@/utils/const";
import { IS_MINIZO } from "@/config/variant";
import { clearRememberedDates } from "@/lib/mypage/diaryDateMemory";
import {
  adoptOnNextReplace,
  bindNavOwner,
  currentEntry,
  expectBack,
  getNavStore,
  isBackTrackerInstalled,
  normalizeLocationKey,
  readOverride,
  recordOverride,
  recordTitle,
  recordTransient,
  resolveBack,
  subscribeNav,
  type NavEntry,
  type NavStore,
  type ScreenOverride,
} from "@/lib/nav/backTracker";
import { screenLabel } from "@/lib/nav/screens";

/** Chrome（RootLayout）の外で描かれたときのホーム。画面名を直書きしない決まり（PRODUCT_DEV-748）に合わせて variant で分ける */
const FALLBACK_HOME = IS_MINIZO
  ? { to: ROUTE_MEMO, label: "メモ" }
  : { to: ROUTE_TOP, label: "記録一覧" };

/** 記録の今の状態と、今の画面の枠 */
const useNavSnapshot = (): { key: string; entry: NavEntry | undefined; store: NavStore } => {
  const location = useLocation();
  const store = useSyncExternalStore(subscribeNav, getNavStore, getNavStore);
  const key = normalizeLocationKey(location.key);
  return { key, entry: currentEntry(store, key), store };
};

export interface OriginBackNav {
  /** 記録が動いている・管理画面ではない（← を来た画面へ戻す画面か） */
  enabled: boolean;
  /** ← の横に出す画面名（戻り先の画面名。分からなければホームの画面名） */
  label: string;
  /** ← を押したとき。`state` は着いた画面の location.state に載せる（保存後の通知など） */
  back: (opts?: { state?: unknown }) => void;
  /** 来た画面が分かっているか */
  hasOrigin: boolean;
  /**
   * ← で戻る先の記録の枠 id（分からなければ null）。戻った画面はこの枠を引き継ぐので、
   * 戻る前に「戻った先で開く状態」をこの枠に覚えさせられる（手帳のタイル。PRODUCT_DEV-1097）
   */
  originEntryId: string | null;
}

/**
 * 左上の ← を「来た画面へ戻る」にする。
 *
 * - 来た画面が分かれば、履歴を戻る（新しい履歴を積まない）。戻れなければその画面の最新の URL へ置き換える
 * - 分からなければ（URL 直打ち・通知メール・別タブ）ホームへ
 */
export const useBackNav = (): OriginBackNav => {
  const navigate = useNavigate();
  const home = useOptionalHomeNav() ?? FALLBACK_HOME;
  const rootData = useRouteLoaderData("root");
  const { key, entry, store } = useNavSnapshot();
  const target = useMemo(() => resolveBack(store, key), [store, key]);

  // root の外（画面だけを描くテスト）・記録が動いていない・管理画面 は今までどおり
  const enabled = !!rootData && isBackTrackerInstalled() && entry?.screen !== "admin";

  const label =
    target.kind === "none"
      ? home.label
      : (target.target.override?.title ??
        target.target.title ??
        (target.target.screen
          ? screenLabel(target.target.screen, { isMinizo: IS_MINIZO })
          : "戻る"));

  const back = useCallback(
    (opts?: { state?: unknown }) => {
      const state = opts?.state;
      if (target.kind === "none") {
        navigate(home.to, state !== undefined ? { state } : undefined);
      } else if (target.kind === "history") {
        expectBack({ expect: target.target.id, fallbackUrl: target.target.url, state });
        navigate(target.delta);
      } else {
        adoptOnNextReplace(target.target.id);
        navigate(target.url, { replace: true, ...(state !== undefined ? { state } : {}) });
      }
    },
    [target, navigate, home.to],
  );

  return {
    enabled,
    label,
    back,
    hasOrigin: target.kind !== "none",
    originEntryId: target.kind === "none" ? null : target.target.id,
  };
};

/**
 * 今の画面のタイトルを記録する。タイトルが中身で変わる画面（商談詳細の商談名・顧客詳細の顧客名など）で
 * 呼ぶと、この画面へ戻ってくる ← の ○○ がそのタイトルになる。空のあいだ（読み込み中）は記録しない。
 */
export const useBackTitle = (title: string | null | undefined): void => {
  const location = useLocation();
  useEffect(() => {
    const t = title?.trim();
    if (t) recordTitle(location.key, t);
  }, [location.key, title]);
};

/**
 * 画面の中で開いている状態（商談一覧で検索を開いている、など）を記録する。
 * 開いているあいだは、この画面へ戻ってくる ← が `title` を出し、戻ったあと `url` へ置き換える
 * （検索条件つきの URL ならオーバーレイが開き直る）。`null` のあいだは何もしない
 * （画面を離れたときに消えないよう、閉じたときに `useScreenOverrideControls().clear()` で明示的に消す）。
 */
export const useScreenOverride = (override: ScreenOverride | null): void => {
  const location = useLocation();
  const url = override?.url;
  const title = override?.title;
  const closeToOrigin = override?.closeToOrigin;
  useEffect(() => {
    if (url && title) recordOverride(location.key, { url, title, closeToOrigin });
  }, [location.key, url, title, closeToOrigin]);
};

/** 画面の中の状態の記録を読む・消す（開いた画面側が閉じるときに使う） */
export const useScreenOverrideControls = (): {
  read: () => ScreenOverride | undefined;
  clear: () => void;
} => {
  const location = useLocation();
  return useMemo(
    () => ({
      read: () => readOverride(location.key),
      clear: () => recordOverride(location.key, null),
    }),
    [location.key],
  );
};

/**
 * 画面の中の状態で一時的な画面になっているあいだ（移動先を選んでいる商談一覧・移動を確定する商談フォルダ）、
 * この画面を戻り先にしない。ここから先へ進んだ画面の ← は、この画面を飛ばしてその前の画面へ戻る。
 */
export const useTransientScreen = (active: boolean): void => {
  const location = useLocation();
  useEffect(() => {
    recordTransient(location.key, active);
  }, [location.key, active]);
};

/** 今の画面の記録の枠 id（手帳の日付の記憶が「戻ってきたか」を見分けるのに使う） */
export const useNavEntryId = (): string | null => useNavSnapshot().entry?.id ?? null;

/**
 * ログインした人を来た画面の記録に結び付ける（RootLayout の `NavOwnerSync` から呼ぶ）。
 * 前に記録した人と違えば記録を捨てる。ログアウトを通らずに入れ替わったとき（トークン切れなど）の備え。
 */
export const useNavOwnerSync = (): void => {
  const data = useRouteLoaderData("root") as
    | { auth?: { currentTenantId?: string; user?: { id?: string } | null } | null }
    | undefined;
  const userId = data?.auth?.user?.id;
  const owner = userId ? `${data?.auth?.currentTenantId ?? ""}:${userId}` : "";
  useEffect(() => {
    if (owner && bindNavOwner(owner)) clearRememberedDates();
  }, [owner]);
};
